/** How clock times are displayed app-wide. */
export type TimeFormat = '24h' | '12h';

/** Parse an "HH:MM" string into hours/minutes (defaults 0 on malformed input). */
export function parseHHMM(value: string): { h: number; m: number } {
  const [h, m] = (value || '').split(':').map(Number);
  return { h: Number.isFinite(h) ? h : 0, m: Number.isFinite(m) ? m : 0 };
}

/** Render an "HH:MM" (24h) value for display in the chosen format. */
export function formatTime(value: string, format: TimeFormat = '24h'): string {
  const { h, m } = parseHHMM(value);
  const mm = m.toString().padStart(2, '0');
  if (format === '12h') {
    const period = h < 12 ? 'AM' : 'PM';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:${mm} ${period}`;
  }
  return `${h.toString().padStart(2, '0')}:${mm}`;
}

function calculateBlockDuration(startTime: string, endTime: string): number {
  const [startHour, startMinute] = startTime.split(':').map(Number);
  const [endHour, endMinute] = endTime.split(':').map(Number);
  
  let duration = (endHour * 60 + endMinute) - (startHour * 60 + startMinute);
  if (duration < 0) {
    duration += 24 * 60; // Add 24 hours if end time is on next day
  }
  
  return duration;
}

export function calculateTotalTime(blocks: { startTime: string; endTime: string; }[]): number {
  return blocks.reduce((total, block) => {
    return total + calculateBlockDuration(block.startTime, block.endTime);
  }, 0);
}

export function formatMinutes(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h${mins ? ` ${mins}m` : ''}`;
}

/**
 * Live stopwatch/countdown format from seconds. Avoids the confusing "0h":
 *   < 1 hour  → "m:ss"   (e.g. 0:45, 12:34)
 *   ≥ 1 hour  → "h:mm:ss" (e.g. 1:02:30)
 */
export function formatClock(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const ss = sec.toString().padStart(2, '0');
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${ss}`;
  return `${m}:${ss}`;
}

/**
 * Render a Date's wall-clock time (hours:minutes) in the chosen format — used for
 * the routine timer's EFT (estimated finish time). Reuses formatTime's HH:MM rules.
 */
export function formatClockTime(date: Date, format: TimeFormat = '12h'): string {
  return formatTime(`${date.getHours()}:${date.getMinutes()}`, format);
}

export function splitSkillNameIntoLines(skillName: string, maxLines: number = 4): string[] {
  const words = skillName.split(/\s+/).filter(word => word.length > 0);

  if (words.length === 0) return [];

  const limitedWords = words.slice(0, maxLines);

  if (words.length > maxLines) {
    const lastWord = limitedWords[limitedWords.length - 1];
    limitedWords[limitedWords.length - 1] = lastWord.length > 8
      ? `${lastWord.slice(0, 6)}...`
      : lastWord + '...';
  }

  return limitedWords;
}